import { CalendarDays, FileText } from "lucide-react";
import type { ReactNode } from "react";
import { StatValueSkeleton } from "../../../components/common";

type NewsStatisticCard = {
  label: string;
  value: string | number;
  icon?: ReactNode;
};

type NewsStatisticsSectionProps = {
  stats?: NewsStatisticCard[];
  loading?: boolean;
};

const getGridClassName = (count: number) => {
  if (count === 2) return "grid-cols-1 sm:grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
  if (count === 5) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5";
  return "grid-cols-1 sm:grid-cols-2 md:grid-cols-4";
};

export function buildNewsStatisticsCards(
  totalLabel: string,
  thisWeekLabel: string,
  totalValue: string | number,
  thisWeekValue: string | number,
) {
  return [
    {
      label: totalLabel,
      value: totalValue,
      icon: <FileText className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
    },
    {
      label: thisWeekLabel,
      value: thisWeekValue,
      icon: <CalendarDays className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
    },
  ];
}

const NewsStatisticsSection = ({ stats, loading = false }: NewsStatisticsSectionProps) => {
  if (!stats?.length) return null;

  return (
    <div className={`mb-4 grid gap-4 ${getGridClassName(stats.length)}`}>
      {stats.map((stat, idx) => (
        <div
          key={`${stat.label}-${idx}`}
          className="relative rounded-lg border border-gray-200 bg-white p-4 dark:border-[#394150] dark:bg-[#212936]"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {loading ? (
                <div className="text-2xl font-bold">
                  <StatValueSkeleton />
                </div>
              ) : (
                <div className="text-2xl font-bold text-foreground">{stat.value}</div>
              )}
              <div className="mb-1 text-sm text-foreground">{stat.label}</div>
            </div>
            {stat.icon ? (
              <div className="ml-4 flex h-8 w-8 items-center justify-center rounded-lg bg-[#F2FCFA] dark:bg-emerald-900/30">
                {stat.icon}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default NewsStatisticsSection;
